import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAuthService } from '../services/adminAuthService';
import { apiService } from '../services/apiService';
import './AdminLogin.css';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loginAdmin, clearError } from '../store/slices/authSlice';

interface LoginForm {
  username: string;
  password: string;
}

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  
  // Load saved preferences from localStorage
  const getSavedForm = (): LoginForm => {
    try {
      const saved = localStorage.getItem('adminLoginForm');
      if (saved) {
        const parsed = JSON.parse(saved);
        return { username: parsed.username || '', password: parsed.password || '' };
      }
    } catch (e) {
      console.error('Error loading saved form:', e);
    }
    return { username: 'admin@banking.com', password: 'admin123' };
  };
  
  const getSavedLoginType = (): boolean | null => {
    try {
      const saved = localStorage.getItem('adminLoginType');
      if (!saved) return null;
      return saved === 'session';
    } catch (e) {
      return null;
    }
  };
  
  const [form, setForm] = useState<LoginForm>(getSavedForm());
  const [otpCode, setOtpCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [useSession, setUseSession] = useState(true);
  const [showLoginTypeSelector, setShowLoginTypeSelector] = useState(true);
  const [loginDisplaySettingsLoading, setLoginDisplaySettingsLoading] = useState(true);
  const [checkingExistingSession, setCheckingExistingSession] = useState(true);
  const [requiresOtp, setRequiresOtp] = useState(false); // Track if OTP is required
  const dispatch = useAppDispatch();
  
  // Use Redux error state like retail Login does
  const { error, isLoading } = useAppSelector(state => state.auth);
  
  // Save form data to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('adminLoginForm', JSON.stringify(form));
    } catch (e) {
      console.error('Error saving form:', e);
    }
  }, [form]);
  
  // Save login type preference to localStorage when it changes
  useEffect(() => {
    if (!showLoginTypeSelector) return;
    try {
      localStorage.setItem('adminLoginType', useSession ? 'session' : 'jwt');
    } catch (e) {
      console.error('Error saving login type:', e);
    }
  }, [useSession, showLoginTypeSelector]);

  useEffect(() => {
    const run = async () => {
      const redirected = await checkExistingAuth();
      if (!redirected) {
        setCheckingExistingSession(false);
      }
    };
    void run();
  }, []);

  // Load login display settings (public endpoint)
  useEffect(() => {
    const loadLoginDisplaySettings = async () => {
      try {
        const response = await apiService.get<{
          defaultLoginType?: string;
          showAdminLoginTypeSelector?: boolean;
        }>('/api/public/auth/login-display-settings');

        if (response.success && response.data) {
          const defaultUseSession = response.data.defaultLoginType === 'SESSION';
          const showSelector = response.data.showAdminLoginTypeSelector !== false;
          const savedType = getSavedLoginType();
          setShowLoginTypeSelector(showSelector);
          setUseSession(showSelector && savedType !== null ? savedType : defaultUseSession);
          return;
        }
      } catch (e) {
        console.error('Error loading admin login display settings:', e);
      }

      const savedType = getSavedLoginType();
      setUseSession(savedType !== null ? savedType : true);
      setShowLoginTypeSelector(true);
      setLoginDisplaySettingsLoading(false);
      return;
    };

    const run = async () => {
      await loadLoginDisplaySettings();
      setLoginDisplaySettingsLoading(false);
    };

    void run();
  }, []);

  // Check error message and set requiresOtp if OTP is required (like retail Login)
  useEffect(() => {
    if (error && (error.includes('OTP required') || error.includes('OTP code') || error.includes('Two-factor authentication'))) {
      setRequiresOtp(true);
    }
  }, [error]);

  // Helper: post-login redirect. Subscription access is centrally enforced by AdminSubscriptionGate.
  const navigateAfterLogin = async (role?: string) => {
    if (role === 'SUPER_ADMIN') {
      const returnTab = sessionStorage.getItem('adminReturnTab');
      const returnData = sessionStorage.getItem('adminReturnData');
      sessionStorage.removeItem('adminReturnTab');
      sessionStorage.removeItem('adminReturnData');
      if (returnTab) {
        let url = `/admin/dashboard?tab=${encodeURIComponent(returnTab)}`;
        if (returnData) url += `&data=${encodeURIComponent(returnData)}`;
        navigate(url);
      } else {
        navigate('/admin/dashboard?tab=dashboard');
      }
      return;
    }
    const returnTab = sessionStorage.getItem('adminReturnTab');
    const returnData = sessionStorage.getItem('adminReturnData');
    sessionStorage.removeItem('adminReturnTab');
    sessionStorage.removeItem('adminReturnData');
    if (returnTab) {
      let url = `/admin/dashboard?tab=${encodeURIComponent(returnTab)}`;
      if (returnData) url += `&data=${encodeURIComponent(returnData)}`;
      navigate(url, { replace: true });
    } else {
      navigate('/admin/dashboard?tab=dashboard', { replace: true });
    }
  };

  const checkExistingAuth = async (): Promise<boolean> => {
    const currentAdmin = adminAuthService.getCurrentAdmin();
    // Use the same strict check as AdminDashboard to avoid bouncing back
    // on a stale/partial session object (e.g. has data but no role).
    if (currentAdmin && currentAdmin.username && currentAdmin.role) {
      await navigateAfterLogin(currentAdmin.role);
      return true;
    }
    return false;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    // Clear Redux error if it's not OTP-related
    if (error && !requiresOtp && !error.includes('OTP') && !error.includes('Two-factor')) {
      dispatch(clearError());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError()); // Clear any previous errors
    
    try {
      const result = await dispatch(loginAdmin({ 
        username: form.username, 
        password: form.password, 
        useSession,
        otpCode: requiresOtp ? otpCode : undefined
      }));
      
      if (loginAdmin.fulfilled.match(result)) {
        setRequiresOtp(false); // Reset OTP requirement on success
        setOtpCode(''); // Clear OTP code
        const role = result.payload?.adminUser?.role;
        await navigateAfterLogin(role);
      } else if (loginAdmin.rejected.match(result)) {
        // Error is automatically set in Redux by the thunk
        // The useEffect will detect OTP errors and set requiresOtp
        const errorMessage = result.payload as string;
        if (errorMessage && (errorMessage.includes('OTP required') || errorMessage.includes('OTP code') || errorMessage.includes('Two-factor authentication'))) {
          setRequiresOtp(true);
        }
      }
    } catch (error) {
      // Error handling is done by the thunk
      console.error('Login error:', error);
    }
  };

  const navigateToSetup = () => {
    navigate('/admin/setup');
  };

  if (isLoading || loginDisplaySettingsLoading || checkingExistingSession) {
    return (
      <div className="min-h-screen bg-zazoo-light-grey flex items-center justify-center px-6">
        <p className="font-nunito text-gray-600">Retrieving session…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zazoo-light-grey flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="h-16 w-16 bg-zazoo-accent-pink rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-3xl font-nunito font-bold text-gray-900">Zogzila Admin Login v2.0</h2>
          <p className="mt-2 text-sm font-nunito text-gray-600">Administrator access to banking platform</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="username" className="block text-sm font-nunito font-semibold text-gray-700 mb-2">
                Admin Username
              </label>
              <input
                id="username"
                type="text"
                name="username"
                value={form.username}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mukta text-sm focus:outline-none focus:ring-2 focus:ring-zazoo-accent-pink focus:border-transparent"
                placeholder="admin@banking.com"
                autoComplete="username"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-nunito font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleInputChange}
                  required
                  disabled={isLoading}
                  className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg font-mukta text-sm focus:outline-none focus:ring-2 focus:ring-zazoo-accent-pink focus:border-transparent"
                  placeholder="admin123"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>


            {/* OTP Code Field - Shown when 2FA is required */}
            {requiresOtp ? (
              <div>
                <label htmlFor="otpCode" className="block text-sm font-nunito font-semibold text-gray-700 mb-2">
                  Two-Factor Authentication Code
                </label>
                <input
                  id="otpCode"
                  type="text"
                  value={otpCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
                    setOtpCode(value);
                  }}
                  required={requiresOtp}
                  disabled={isLoading}
                  maxLength={6}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mukta text-sm text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-zazoo-accent-pink focus:border-transparent"
                  placeholder="000000"
                  autoComplete="one-time-code"
                />
                <p className="mt-2 text-xs font-nunito text-gray-500">
                  Enter the 6-digit code from your Google Authenticator app
                </p>
              </div>
            ) : null}

            {/* Authentication Type Selector */}
            {showLoginTypeSelector && (
              <div className="form-group">
                <label className="block text-sm font-nunito font-semibold text-gray-700 mb-2">
                  Login Type
                </label>
                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="adminAuthType"
                      value="web"
                      checked={useSession}
                      onChange={() => setUseSession(true)}
                      disabled={isLoading}
                      className="h-4 w-4 text-zazoo-accent-pink focus:ring-zazoo-accent-pink border-gray-300"
                    />
                    <span className="ml-2 text-sm font-nunito text-gray-700">Web (Session)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="adminAuthType"
                      value="api"
                      checked={!useSession}
                      onChange={() => setUseSession(false)}
                      disabled={isLoading}
                      className="h-4 w-4 text-zazoo-accent-pink focus:ring-zazoo-accent-pink border-gray-300"
                    />
                    <span className="ml-2 text-sm font-nunito text-gray-700">API (JWT)</span>
                  </label>
                </div>
              </div>
            )}
            
            {error && (
              <div className="flex items-start gap-3 p-4 bg-zazoo-red bg-opacity-10 border border-zazoo-red rounded-lg">
                <svg className="w-5 h-5 text-zazoo-red flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="font-nunito text-zazoo-red text-sm">{error}</p>
              </div>
            )}
            
            <button
              type="submit"
              disabled={isLoading || !form.username || !form.password || (requiresOtp && otpCode.length !== 6)}
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-mukta font-medium text-white bg-zazoo-accent-pink hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-zazoo-accent-pink disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {requiresOtp ? 'Verifying...' : 'Signing in...'}
                </>
              ) : (
                requiresOtp ? 'Verify OTP Code' : 'Sign in as Admin'
              )}
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <button 
              className="font-nunito text-zazoo-accent-blue hover:text-zazoo-accent-pink"
              onClick={() => navigate('/login')}
            >
              Back to User Login
            </button>
          </div>
        </div>
        
        <div className="text-center">
          <p className="text-xs font-nunito text-gray-500">
            {/* Demo credentials: admin@banking.com / admin123 */}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin; 